import { ProductCode } from './product.model';

/**
 * Product sorting order map for consistent product ordering across the application.
 * Lower numbers appear first in sorted lists (most expensive/premium products first).
 *
 * Number families:
 * - Value based pricing: -1000
 * - Special products: -900 family (priority at top)
 * - JobScout24: 0-99 family
 * - Jobs.ch: 100-199 family
 * - JobUp.ch: 200-299 family
 * - Apprenticeships: 300-399 family
 *
 * Within each family: T-shirt sizing (XL → L → M → S → XS) - Most expensive → Least expensive
 *
 * Used by:
 * - Product offering displays
 * - Credit aggregation and display
 * - Product comparison views
 */
export const PRODUCT_SORTING_ORDER: Record<ProductCode, number> = {
  [ProductCode.VBPPAID]: -1000,
  [ProductCode.VBPFREE]: -999,

  // Special products (-900 family - priority at top)
  [ProductCode.PAYPERPERFORMANCE]: -901,
  [ProductCode.JOBUPOFFSITE]: -902,
  [ProductCode.JOBSOFFSITE]: -903,
  [ProductCode.JOBUPSPONTANEOUSAPPLICATION]: -904,
  [ProductCode.JOBSSPONTANEOUSAPPLICATION]: -905,
  [ProductCode.JOBSGIFTEDFREE]: -906, // Free gifted product (jobs.ch)
  [ProductCode.JOBUPGIFTEDFREE]: -907, // Free gifted product (jobup.ch)

  // JobScout24 products (0-99 family)
  [ProductCode.JS24ADVANCED]: 1, // Most premium
  [ProductCode.JS24BASIC]: 2,
  [ProductCode.JS24PLUS]: 3,
  [ProductCode.JS24SOLO]: 4,
  [ProductCode.JS24FREE]: 5, // Free

  // Jobs.ch products (100-199 family)
  // Older products (100-109)
  [ProductCode.JOBSSTARTER]: 100,
  [ProductCode.JOBSSTANDARD]: 101, // join integration, not using on UI
  [ProductCode.JOBSFLEX]: 102, // used only for upsell landing page
  [ProductCode.JOBSLIMITED]: 103,

  // 2022 offering (110-119) - T-shirt sizing: XL → L → M → S → XS
  [ProductCode.JOBSOFFER22XL]: 110, // XL - Ultimate Plus (most expensive)
  [ProductCode.JOBSOFFER22L]: 111, // L - Ultimate
  [ProductCode.JOBSOFFER22M]: 112, // M - Advanced
  [ProductCode.JOBSOFFER22S]: 113, // S - Basic
  [ProductCode.JOBSOFFER22XS]: 109, // XS - Lite (least expensive)
  [ProductCode.JOBSOFFER22FREE]: 115, // Free

  // JobUp.ch products (200-299 family)
  // 2024 offering (200-209) - T-shirt sizing equivalent: Ultimate Plus → Ultimate → Advanced → Basic → Lite
  [ProductCode.JOBUP24ULTIMATEPLUS]: 200, // Ultimate Plus (most expensive)
  [ProductCode.JOBUP24ULTIMATE]: 201, // Ultimate
  [ProductCode.JOBUP24ADVANCED]: 202, // Advanced
  [ProductCode.JOBUP24BASIC]: 203, // Basic
  [ProductCode.JOBUP24LITE]: 199, // Lite (least expensive)
  [ProductCode.JOBUP24FREE]: 205, // Free

  // Apprenticeship products (300-399 family)
  [ProductCode.JOBUPAPPRENTICESHIP]: 300,
  [ProductCode.JOBSAPPRENTICESHIP]: 301,
};

/**
 * Sort products by their defined order (most expensive first).
 * Products not in the sorting map will appear last.
 */
export const sortProductsByOrder = (a: ProductCode, b: ProductCode): number => {
  const aOrder = PRODUCT_SORTING_ORDER[a] ?? 999;
  const bOrder = PRODUCT_SORTING_ORDER[b] ?? 999;
  return aOrder - bOrder;
};
